import { defineStore } from 'pinia'
import {
	// readonly,
	shallowReadonly,
	toRaw,
} from 'vue'
import {
	INFLUXDB,
	readStationMetric,
	readStationMetrics,
} from '@/api/api.js'
import {
	periodDays,
	addSecondsToDate,
	// dateTimeToString,
	// firstYearDay,
	// lastYearDay,
	// dayStart,
	// dayEnd,
} from '@/helpers.js'
import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { ElectricMeter } from '@/models/electricMeter.js'
import { ModemUnit } from '@/models/modemUnit.js'




// Max concurrent requests at a time
const MAX_CONCURRENT_REQUESTS = 1
const REQUEST_TIMEOUT = 10000
// Period in seconds
const ACCURATE_METRICS_MAX_PERIOD = 60 * 60 * 6

export const useStationsHistoryStore = defineStore('stationsHistoryStore', {
	state: () => ({
		// Access keys for rest API (mongoDB)
		_api: {
			login: null,
			password: null,
		},

		// Active request counter
		activeReqCounter: 0,
		// Request queue for waiting when limit is reached
		requestQueue: [],

		// Increments on any data update
		updateCounter: 0,

		// History metrics
		// Map {
		//		stationMetricsId: Map {
		//			unit_name: Map {
		//				metricName: Object {
		// 					time: Object {
		// 						metricName: Object {
		// 							value: value,
		//							median: value,
		// 							min: value,
		//							max: value,
		// 						},
		// 						... 
		// 					},
		// 					...
		// 				},
		// 			},
		// 		},
		// }
		metrics: new Map(),

		// Store list of already loaded date ranges per metric and load mode
		metricRangesInData: new Map(),

		// Cached getMetricHistory for station
		// Map {
		// 		stationMetricsId: Map {
		//			getMetricHistory_parms: Array from getMetricHistory
		//		}
		// }
		metricHistoryCache: new Map(),
	}),

	getters: {
		activeRequestsCounter() {
			return this.activeReqCounter
		},

		updateTriger() {
			return this.updateCounter
		}
	},

	actions: {
		setApiAccessKeys(login, password) {
			this._api.login = login
			this._api.password = password
		},

		// TODO: Optimazation https://uk.javascript.info/fetch-abort
		/**
		 * Update metric values in state storage (this.data)
		 * @param {*} stationMetricsId metrics id for db and this.data
		 * @param {*} metrics Array of [unit, metric]
		 * @param {*} timeFrom start Date
		 * @param {*} timeTo end Date
		 * @param {*} withOptimization if true then skips same values but not older than an hour
		 */
		async loadMetrics(
			stationMetricsId,
			metrics,
			timeFrom,
			timeTo,
			withOptimization = true,
		) {
			// console.log(`updateMetrics ${dateTimeToString(timeFrom, false)} ${dateTimeToString(timeTo, false)}`)
			this._prepareStationStore(stationMetricsId)
			this._initMetricCache(stationMetricsId)

			let accurateReadMetrics = []
			let approximateReadMetrics = []

			// If calculated metrics in request
			if (metrics.some(([unitName, metricName]) => unitName == INFLUXDB.station_metrics.power_unit && metricName == 'outPower')
				|| metrics.some(([unitName, metricName]) => unitName == INFLUXDB.station_metrics.power_unit && metricName == 'resistancePolarizationCircle')
			) {
				// Add base metrics for calculating if not added
				if (!metrics.some(([unitName, metricName]) => unitName == INFLUXDB.station_metrics.power_unit
					&& metricName == PowerUnit.metricKeys.iOut)
				) {
					metrics.push([INFLUXDB.station_metrics.power_unit, PowerUnit.metricKeys.iOut])
				}
				if (!metrics.some(([unitName, metricName]) => unitName == INFLUXDB.station_metrics.power_unit
					&& metricName == PowerUnit.metricKeys.uOut)
				) {
					metrics.push([INFLUXDB.station_metrics.power_unit, PowerUnit.metricKeys.uOut])
				}
			}

			// Distributing metrics across acurate and approximate reading arrays
			const period = (timeTo.getTime() - timeFrom.getTime()) / 1000
			if (period <= ACCURATE_METRICS_MAX_PERIOD) {
				accurateReadMetrics = metrics
			} else {
				// Sort metrics to accurate or aproximate reading
				// Iterate all metrics
				for (const [unitName, metricName] of metrics) {
					if (this._isBinaryMetric(metricName) 
						|| this._isOnlyAccurateMetric(metricName)
					) {
						accurateReadMetrics.push([unitName, metricName])
					} else {
						approximateReadMetrics.push([unitName, metricName])
					}
				}
			}

			// Read metrics from acurate and approximate arrays
			// Raed accurate metrics month by month (optimization for influx db)
			const secondsPerMonth = 60 * 60 * 24 * 31
			let time1 = new Date(timeFrom)
			let time2
			for (let i = 0; i <= periodDays(timeFrom, timeTo) / 30; i++) {
				// Update month period
				time2 = addSecondsToDate(time1, secondsPerMonth)
				
				// Accurate reading metrics one by one
				for (const [unitName, metricName] of accurateReadMetrics) {
					if (this._isMetricRangeLoaded(stationMetricsId, unitName, metricName, 'accurate', time1, time2)) {
						continue
					}

					// If store deleted
					if (!this.metrics.get(stationMetricsId)) {
						continue
					}

					await this._executeWithQueueLimit(async () => {
						const readResult = await this._accurateReadMetric(
							stationMetricsId,
							unitName,
							metricName,
							time1,
							time2,
							this._isNoOptimizatonMetric(metricName) ? false : withOptimization,
						)

						if (readResult) {
							this._markMetricRangeLoaded(
								stationMetricsId,
								unitName,
								metricName,
								'accurate',
								time1,
								time2,
							)
						}
					})
				}

				time1 = new Date(time2)			
				// Escape
				if (time2 >= timeTo) {
					break
				}
			}

			// Read approximate metrics
			// Filter approximate metrics already loaded for this exact range
			const toReadApprox = approximateReadMetrics.filter(([unitName, metricName]) => 
				!this._isMetricRangeLoaded(
					stationMetricsId,
					unitName,
					metricName,
					'approximate',
					timeFrom,
					timeTo,
				)
			)

			if (toReadApprox.length > 0) {
				// Approximate reading
				await this._executeWithQueueLimit(async () => {
					const readResult = await this._approximateReadMetric(
						stationMetricsId,
						toReadApprox,
						timeFrom,
						timeTo,
					)

					if (readResult) {
						for (const [unitName, metricName] of toReadApprox) {
							this._markMetricRangeLoaded(
								stationMetricsId,
								unitName,
								metricName,
								'approximate',
								timeFrom,
								timeTo,
							)
						}
					}
				})
			}

			// Increment counter for trigerring update
			this.updateCounter++
		},

		// Execute request with queue management
		async _executeWithQueueLimit(requestFn) {
			// Wait if limit reached
			while (this.activeReqCounter >= MAX_CONCURRENT_REQUESTS) {
				await new Promise(resolve => {
					this.requestQueue.push(resolve)
				})
			}

			this.activeReqCounter++

			try {
				await requestFn()
			} finally {
				this.activeReqCounter--

				// Process next request in queue if exists
				if (this.requestQueue.length > 0) {
					const nextResolve = this.requestQueue.shift()
					nextResolve()
				}
			}
		},

		async _accurateReadMetric(
			stationMetricsId,
			unitId,
			metricId,
			startDate,
			endDate,
			withOptimization
		) {
			let dataPoints = []

			const reqResult = await readStationMetric(
				stationMetricsId,
				unitId,
				metricId,
				dataPoints,
				startDate,
				endDate == null ? 'now' : endDate,
				11000,
			).catch(err => {
				console.error(`Error on accurate read station metric [${unitId}:${metricId}]:`, err)
			})

			// console.log(`Accurate Read ${metricId} result ${reqResult} with ${dataPoints.length} points`)

			if (!reqResult) {
				return false
			}

			let currentState = null
			let currentTime = null

			for (const point of dataPoints) {
				// If this station storage has been removed before read request finished
				if (!this.metrics.get(stationMetricsId)) {
					console.warn('Station storage was removed before read request was finished!')
					break
				}

				// Skip same points but not older than an hour
				if (withOptimization
					&& currentState == point[metricId]
					&& currentTime?.getHours() == point.time.getHours()
				) {
					continue
				} else {
					currentState = point[metricId]
					currentTime = point.time

					if (this.metrics.get(stationMetricsId)?.get(unitId)?.get(metricId) === undefined) {
						this._initMetricStore(stationMetricsId, unitId, metricId)
					}

					// Save current data point in storage
					this.metrics
						.get(stationMetricsId)
						.get(unitId)
						.get(metricId)[point.time] = {
							value: point[metricId],
						}
				}
			}

			dataPoints.splice(0, dataPoints.length)
			return true
		},

		async _approximateReadMetric(
			stationMetricsId,
			metrics,
			startDate,
			endDate,
			window,
		) {
			// Approximate reading
			const periodDaysCount = periodDays(startDate, endDate == null ? new Date() : endDate)
			const metricsArr = []
			
			const reqResult = await readStationMetrics(
				stationMetricsId,
				[...new Set(metrics.map((v) => v[0]))],
				[...new Set(metrics.map((v) => v[1]))],
				metricsArr,
				startDate,
				endDate,
				window,
				['min', 'max', 'median',],
				REQUEST_TIMEOUT * (periodDaysCount / 365 + 2.0),
			).catch(err => {
				console.error("Error on approximate read station metrics: ", err)
			})

			if (!reqResult) {
				return false
			}

			// Sort by time
			metricsArr.sort((a, b) => a.time.getTime() > b.time.getTime())

			// If this station storage has been removed before read request finished
			if (!this.metrics.get(stationMetricsId)) {
				console.warn('Station storage was removed before read request was finished!')
				metricsArr.splice(0, metricsArr.length)
				return
			}

			// Iteration over time slices
			for (const timePoint of metricsArr) {
				// Iterate all metrics
				for (const [unit, metric] of metrics) {
					const time = timePoint.time
					// Parse metric
					const keyMin = unit + '_' + metric + '_' + 'min'
					const keyMax = unit + '_' + metric + '_' + 'max'
					const keyMedian = unit + '_' + metric + '_' + 'median'

					// If no current metric in time slice
					if (timePoint[keyMedian] === undefined
						&& timePoint[keyMin] === undefined
						&& timePoint[keyMax] === undefined
					) {
						continue
					}

					if (this.metrics.get(stationMetricsId)?.get(unit)?.get(metric) === undefined) {
						this._initMetricStore(stationMetricsId, unit, metric)
					}

					// Save current data point in storage
					this.metrics
						.get(stationMetricsId)
						.get(unit)
						.get(metric)[time] = {
							median: timePoint[keyMedian],
							min: timePoint[keyMin],
							max: timePoint[keyMax],
						}
				}
			}

			metricsArr.splice(0, metricsArr.length)
			return true
		},

		/**
		 * Extract metrics from starage. 
		 * @param {*} stationMetricsId metrics id for db and this.data
		 * @param {*} unitId 
		 * @param {*} metricId 
		 * @param {*} timeFrom start Date
		 * @param {*} timeTo end Date
		 * @returns 
		 */
		getMetricHistory(stationMetricsId, unitId, metricId, timeFrom, timeTo) {
			const timeFilter = (el) => {
				const metricTime = el.time

				if (timeFrom && timeTo) {
					return metricTime >= timeFrom && metricTime <= timeTo
				} else if (!timeFrom && timeTo) {
					return metricTime <= timeTo
				} else if (timeFrom && !timeTo) {
					return metricTime >= timeFrom
				} else {
					return true
				}
			}

			let result
			const requestId = `${stationMetricsId}:${unitId}:${metricId}:${timeFrom?.getTime() ?? ''}:${timeTo?.getTime() ?? ''}`
			const requestCache = this._getMetricCache(stationMetricsId, requestId)
			// If request is cached - return from cache
			if (requestCache.length > 0) {
				return requestCache
			}

			// If calculated metric in request
			if (unitId == INFLUXDB.station_metrics.power_unit && metricId == 'outPower'
				|| unitId == INFLUXDB.station_metrics.power_unit && metricId == 'resistancePolarizationCircle'
			) {
				const iOut = this.getMetricHistory(
					stationMetricsId,
					INFLUXDB.station_metrics.power_unit,
					PowerUnit.metricKeys.iOut,
					timeFrom,
					timeTo,
				)

				if (iOut.length == 0) {
					return []
				}

				result = iOut.map((iOutMetric) => {
					let value, median, min, max
					const uOutMetric = this.getPastMetricValue(
						stationMetricsId,
						INFLUXDB.station_metrics.power_unit,
						PowerUnit.metricKeys.uOut,
						iOutMetric.time,
					)
					if (!uOutMetric) {
						return { time: iOutMetric.time, }
					}

					if (metricId == 'outPower') {
						value = uOutMetric.value * iOutMetric.value
						median = uOutMetric.median * iOutMetric.median
						min = uOutMetric.min * iOutMetric.min
						max = uOutMetric.max * iOutMetric.max
					} else if (metricId == 'resistancePolarizationCircle') {
						value = uOutMetric.value == 0 ? undefined : (uOutMetric.value / iOutMetric.value)
						median = uOutMetric.median == 0 ? undefined : (uOutMetric.median / iOutMetric.median)
						min = uOutMetric.min == 0 ? undefined : (uOutMetric.min / iOutMetric.min)
						max = uOutMetric.max == 0 ? undefined : (uOutMetric.max / iOutMetric.max)
					} else {
						return { time: iOutMetric.time, }
					}
					
					return {
						time: iOutMetric.time,
						...(Number.isFinite(value) ? { value } : {}),
						...(Number.isFinite(median) ? { median } : {}),
						min,
						max,
					}
				})
			} else {
				const metric = this.metrics.get(stationMetricsId)?.get(unitId)?.get(metricId)

				result = !metric
					? []
					: shallowReadonly(
						Object.entries(metric)
						.map(([time, item]) => Object.assign({time: new Date(time)}, item))
						.filter(timeFilter)
						.sort((a, b) => a.time - b.time)
					)
			}

			requestCache.push(...toRaw(result))
			return result
		},

		/*
		getMetricValue(stationMetricsId, unitName, metricName, timePoint) {
			// If calculated metric in request
			if (unitName == INFLUXDB.station_metrics.power_unit && metricName == 'outPower'
				|| unitName == INFLUXDB.station_metrics.power_unit && metricName == 'resistancePolarizationCircle'
			) {
				let value, median, min, max
				const iOut = this.getMetricValue(
					stationMetricsId,
					INFLUXDB.station_metrics.power_unit,
					PowerUnit.metricKeys.iOut,
					timePoint,
				)

				const uOut = this.getMetricValue(
					stationMetricsId,
					INFLUXDB.station_metrics.power_unit,
					PowerUnit.metricKeys.uOut,
					timePoint,
				)

				if (iOut == undefined || uOut == undefined) {
					return undefined
				}

				if (metricName == 'outPower') {
					value = uOut.value * iOut.value
					median = uOut.median * iOut.median
					min = uOut.min * iOut.min
					max = uOut.max * iOut.max
				} else if (metricName == 'resistancePolarizationCircle') {
					value = uOut.value == 0 ? undefined : (uOut.value / iOut.value)
					median = uOut.median == 0 ? undefined : (uOut.median / iOut.median)
					min = uOut.min == 0 ? undefined : (uOut.min / iOut.min)
					max = uOut.max == 0 ? undefined : (uOut.max / iOut.max)
				} else {
					return undefined
				}
				
				return {
					time: timePoint,
					...(Number.isFinite(value) ? { value } : {}),
					...(Number.isFinite(median) ? { median } : {}),
					min,
					max,
				}
			} else {
				return this.metrics.get(stationMetricsId)?.get(unitName)?.get(metricName)?.[timePoint]
			}
		},
		*/

		/**
		 * Retrospective search metric from timePoint time
		 * @param {*} stationMetricsId 
		 * @param {*} unitId 
		 * @param {*} metricId 
		 * @param {*} timePoint 
		 * @param {*} depth - index of value to past. 0 ... Greater means earlier
		 * @returns metric or undefined
		 */
		getPastMetricValue(stationMetricsId, unitId, metricId, timePoint, depth=0) {
			const metrics = this.getMetricHistory(stationMetricsId, unitId, metricId, undefined, timePoint)
			if (!metrics) {
				return undefined
			}

			return metrics.toReversed()[depth]
		},

		/**
		 * Search metric from timePoint time to future/
		 * @param {*} stationMetricsId 
		 * @param {*} unitId 
		 * @param {*} metricId 
		 * @param {*} timePoint 
		 * @param {*} depth - index of value to future. 0 ... Greater means later
		 * @returns metric or undefined
		 */
		getFutureMetricValue(stationMetricsId, unitId, metricId, timePoint, depth=0) {
			const metrics = this.getMetricHistory(stationMetricsId, unitId, metricId, timePoint, undefined)
			if (!metrics) {
				return undefined
			}

			return metrics[depth]
		},

		// Clean all starage and cache
		clean() {
			console.info('Clean stations history storage')
			this.metrics = new Map()
			this.requestQueue = []
			this.metricHistoryCache = new Map()
			this.metricRangesInData = new Map()
		},

		_isBinaryMetric(metricId, /*unit*/) {
			const binaryMetrics = [
				ModemUnit.metricKeys.isConnect,
				ServiceUnit.metricKeys.isConnect,
				PowerUnit.metricKeys.isConnect,
				ElectricMeter.metricKeys.isConnect,
				PowerUnit.metricKeys.fBreakCurrent,
				ServiceUnit.metricKeys.fSecurityCircuit_1,
				ServiceUnit.metricKeys.fSecurityCircuit_2,
				ServiceUnit.metricKeys.fSecurityCircuit_3,
				ServiceUnit.metricKeys.fSecurityCircuit_4,
				ServiceUnit.metricKeys.fSecurityCircuit_5,
				ServiceUnit.metricKeys.fSecurityCircuit_6,
				ServiceUnit.metricKeys.fSecurityCircuitBoxDoor,
				ServiceUnit.metricKeys.fSecurityCircuitShelterDoor,
				ServiceUnit.metricKeys.fExternalPwr,
				PowerUnit.metricKeys.shortCircuitLoad,
				PowerUnit.metricKeys.breakCurrentSensor,
				PowerUnit.metricKeys.breakTemperatureSensor,
				PowerUnit.metricKeys.overheat,
				PowerUnit.metricKeys.lowTrasholdU220,
				PowerUnit.metricKeys.hiTrasholdU220,
				PowerUnit.metricKeys.shortCircuitInvertor,
				PowerUnit.metricKeys.fBreakPotential,
				
			]

			return binaryMetrics.includes(metricId)
		},

		_isOnlyAccurateMetric(metricId, /*unit*/) {
			const accurateMetrics = [
				...Object.values(Station.analyticMetrics),
				PowerUnit.metricKeys.stationStabilizationMode,
				PowerUnit.metricKeys.stationState,
			]

			return accurateMetrics.includes(metricId)
		},

		_isNoOptimizatonMetric(metricId, /*unit*/) {
			const accurateMetrics = [
				...Object.values(Station.analyticMetrics),
				PowerUnit.metricKeys.stationStabilizationMode,
				PowerUnit.metricKeys.stationState,
			]

			return accurateMetrics.includes(metricId)
		},

		// Prepare store for update
		_prepareStationStore(stationMetricsId) {
			// Create station storage if not exists
			if (!this.metrics.has(stationMetricsId)) {
				this._initStationStore(stationMetricsId)
			}

			if (!this.metricRangesInData.has(stationMetricsId)) {
				this.metricRangesInData.set(stationMetricsId, new Map())
			}

			this._initStationCache(stationMetricsId)

			// Delete other station stores if exist
			for (const key of this.metrics.keys()) {
				if (key != stationMetricsId) {
					this.metrics.delete(key)
				}
			}

			// Delete other station caches if exist
			for (const key of this.metricHistoryCache.keys()) {
				if (key != stationMetricsId) {
					this.metricHistoryCache.delete(key)
				}
			}

			// Delete other station range caches if exist
			for (const key of this.metricRangesInData.keys()) {
				if (key != stationMetricsId) {
					this.metricRangesInData.delete(key)
				}
			}
		},

		// Init store from scratch
		_initStationStore(stationMetricsId) {
			// Create station storage
			this.metrics.set(stationMetricsId, new Map())
		},

		_initMetricStore(stationMetricsId, unitId, metricId) {
			// If no container for current unit
			if (!this.metrics.get(stationMetricsId).has(unitId)) {
				this.metrics.get(stationMetricsId).set(unitId, new Map())
			}
			
			// If no container for current metric
			if (!this.metrics.get(stationMetricsId).get(unitId).has(metricId)) {
				this.metrics.get(stationMetricsId).get(unitId).set(metricId, {})
			}
		},

		_isMetricRangeLoaded(stationMetricsId, unitId, metricId, mode, startDate, endDate) {
			const rangeKey = `${unitId}:${metricId}:${mode}`
			const cachedRanges = this._initMetricRangeCache(stationMetricsId, rangeKey)

			return cachedRanges.some((range) =>
				range.startDate.getTime() == startDate.getTime()
				&& range.endDate.getTime() == endDate.getTime()
			)
		},

		_markMetricRangeLoaded(stationMetricsId, unitId, metricId, mode, startDate, endDate) {
			const rangeKey = `${unitId}:${metricId}:${mode}`
			const cachedRanges = this._initMetricRangeCache(stationMetricsId, rangeKey)

			const rangeAlreadyExists = cachedRanges.some((range) =>
				range.startDate.getTime() == startDate.getTime()
				&& range.endDate.getTime() == endDate.getTime()
			)

			if (!rangeAlreadyExists) {
				cachedRanges.push({
					startDate: startDate,
					endDate: endDate,
				})
			}
		},

		_initMetricRangeCache(stationMetricsId, rangeKey) {
			if (!this.metricRangesInData.has(stationMetricsId)) {
				this.metricRangesInData.set(stationMetricsId, new Map())
			}

			const stationRangeCache = this.metricRangesInData.get(stationMetricsId)
			if (stationRangeCache.get(rangeKey) === undefined) {
				stationRangeCache.set(rangeKey, new Array())
			}

			return toRaw(stationRangeCache.get(rangeKey))
		},

		_initStationCache(stationMetricsId) {
			// Clean station cache
			this.metricHistoryCache.set(stationMetricsId, new Map())
		},

		_initMetricCache(stationMetricsId, requestId) {
			if (requestId) {
				this.metricHistoryCache.get(stationMetricsId).set(requestId, new Array())
			} else {
				this.metricHistoryCache.set(stationMetricsId, new Map())
			}
		},

		_getMetricCache(stationMetricsId, requestId) {
			// If no container for current request
			if (this.metricHistoryCache.get(stationMetricsId)?.get(requestId) === undefined) {
				return []
			}

			return toRaw(this.metricHistoryCache.get(stationMetricsId).get(requestId))
		},
	},
})
